import { useState } from 'react';
import Alert from 'react-bootstrap/Alert';
import Button from 'react-bootstrap/Button';
import Form from 'react-bootstrap/Form';
import Modal from 'react-bootstrap/Modal';
import { AuthService } from 'api/services/authService';

interface Props {
  show: boolean;
  onHide: () => void;
}

export default function ChangePasswordModal({ show, onHide }: Props) {
  const [form, setForm]       = useState({ currentPassword: '', newPassword: '', confirmPassword: '' });
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  // Un ojo por campo, no uno general: si fueran uno solo, al revisar la nueva
  // se destaparía también la actual, que no hace falta ver.
  const [verActual, setVerActual] = useState(false);
  const [verNueva, setVerNueva] = useState(false);
  const [verConfirmar, setVerConfirmar] = useState(false);

  const reset = () => {
    setForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
    setVerActual(false);
    setVerNueva(false);
    setVerConfirmar(false);
    setError(null);
    setSuccess(false);
  };

  const handleHide = () => {
    reset();
    onHide();
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setError(null);
    setForm(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (form.newPassword !== form.confirmPassword) {
      setError('Las contraseñas nuevas no coinciden');
      return;
    }

    setLoading(true);
    try {
      await AuthService.changeMyPassword(form.currentPassword, form.newPassword, form.confirmPassword);
      setSuccess(true);
      setForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
    } catch (err: any) {
      setError(err?.response?.data?.metadata?.msg ?? 'Error al cambiar la contraseña');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal show={show} onHide={handleHide} centered>
      <Modal.Header closeButton>
        <Modal.Title>
          <i className="ph ph-lock-key me-2" />
          Cambiar contraseña
        </Modal.Title>
      </Modal.Header>

      {success ? (
        /* ── Estado éxito ── */
        <Modal.Body className="text-center py-4">
          <Alert variant="success" className="mb-0">
            <i className="ph ph-check-circle me-2 fs-5" />
            <strong>¡Contraseña actualizada correctamente!</strong>
          </Alert>
        </Modal.Body>
      ) : (
        /* ── Formulario ── */
        <Form onSubmit={handleSubmit}>
          <Modal.Body>
            {error && (
              <Alert variant="danger" className="py-2">
                <i className="ph ph-warning me-2" />
                {error}
              </Alert>
            )}

            <Form.Group className="mb-3">
              <Form.Label>Contraseña actual</Form.Label>
              <div className="input-group">
                <Form.Control
                  type={verActual ? 'text' : 'password'}
                  name="currentPassword"
                  value={form.currentPassword}
                  onChange={handleChange}
                  required
                  autoFocus
                  disabled={loading}
                />
                <Button
                  variant="outline-secondary"
                  type="button"
                  onClick={() => setVerActual((v) => !v)}
                  disabled={loading}
                  title={verActual ? 'Ocultar' : 'Mostrar'}
                  aria-label={verActual ? 'Ocultar la contraseña' : 'Mostrar la contraseña'}
                  tabIndex={-1}
                >
                  <i className={`ph ${verActual ? 'ph-eye-slash' : 'ph-eye'}`} />
                </Button>
              </div>
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Nueva contraseña</Form.Label>
              <div className="input-group">
                <Form.Control
                  type={verNueva ? 'text' : 'password'}
                  name="newPassword"
                  value={form.newPassword}
                  onChange={handleChange}
                  required
                  minLength={6}
                  disabled={loading}
                />
                <Button
                  variant="outline-secondary"
                  type="button"
                  onClick={() => setVerNueva((v) => !v)}
                  disabled={loading}
                  title={verNueva ? 'Ocultar' : 'Mostrar'}
                  aria-label={verNueva ? 'Ocultar la contraseña' : 'Mostrar la contraseña'}
                  tabIndex={-1}
                >
                  <i className={`ph ${verNueva ? 'ph-eye-slash' : 'ph-eye'}`} />
                </Button>
              </div>
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Confirmar nueva contraseña</Form.Label>
              <div className="input-group">
                <Form.Control
                  type={verConfirmar ? 'text' : 'password'}
                  name="confirmPassword"
                  value={form.confirmPassword}
                  onChange={handleChange}
                  required
                  minLength={6}
                  disabled={loading}
                />
                <Button
                  variant="outline-secondary"
                  type="button"
                  onClick={() => setVerConfirmar((v) => !v)}
                  disabled={loading}
                  title={verConfirmar ? 'Ocultar' : 'Mostrar'}
                  aria-label={verConfirmar ? 'Ocultar la contraseña' : 'Mostrar la contraseña'}
                  tabIndex={-1}
                >
                  <i className={`ph ${verConfirmar ? 'ph-eye-slash' : 'ph-eye'}`} />
                </Button>
              </div>
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={handleHide} disabled={loading}>
              Cancelar
            </Button>
            <Button type="submit" variant="primary" disabled={loading}>
              {loading
                ? <><span className="spinner-border spinner-border-sm me-2" />Guardando...</>
                : <><i className="ph ph-floppy-disk me-1" />Guardar</>}
            </Button>
          </Modal.Footer>
        </Form>
      )}

      {/* Footer del estado éxito */}
      {success && (
        <Modal.Footer>
          <Button variant="success" onClick={handleHide}>
            <i className="ph ph-check me-1" />
            Aceptar
          </Button>
        </Modal.Footer>
      )}
    </Modal>
  );
}
